import React, { useState } from "react";

type Approver = {
  id: string;
  empId?: string; // Add empId as alternative to id
  name: string;
  avatar?: string;
  image?: string; // Add image as alternative to avatar
  status: "Submitted" | "Pending" | "Approved" | "Rejected" | "Confirmed" | "Occupation Submitted" | "Declined";
  stepName?: string; // Optional step name to show above the node
  isMilestone?: boolean;
  phaseColor?: string;
};

// Extended status type for internal rendering
type RenderStatus = Approver["status"] | "Upcoming";

const STATUS_STYLES = {
  Approved: { color: "#10B981", bg: "#F0FDF4", label: "Approved", ringColor: "#10B981", icon: "✓" },
  Confirmed: { color: "#10B981", bg: "#F0FDF4", label: "Confirmed", ringColor: "#10B981", icon: "✓" },
  Pending: { color: "#F59E0B", bg: "#FFFBEB", label: "Pending", ringColor: "#F59E0B", icon: "●" },
  Rejected: { color: "#EF4444", bg: "#FEF2F2", label: "Rejected", ringColor: "#EF4444", icon: "✕" },
  Declined: { color: "#EF4444", bg: "#FEF2F2", label: "Declined", ringColor: "#EF4444", icon: "✕" },
  Submitted: { color: "#6366F1", bg: "#EEF2FF", label: "Submitted", ringColor: "#6366F1", icon: "↑" },
  "Occupation Submitted": { color: "#a855f7", bg: "#f5f3ff", label: "Occupation Submitted", ringColor: "#a855f7", icon: "↑" },
  Upcoming: { color: "#94A3B8", bg: "#F8FAFC", label: "Waiting", ringColor: "#E2E8F0", icon: "" },
};

const ApproverNode: React.FC<{ appr: Approver; renderStatus: RenderStatus; isCurrentPending: boolean; isDisabled?: boolean; phaseColor?: string }> = ({ appr, renderStatus, isCurrentPending, isDisabled, phaseColor }) => {
  const normalizedStatus = (renderStatus && typeof renderStatus === 'string')
    ? (renderStatus === "Occupation Submitted" ? renderStatus : renderStatus.charAt(0).toUpperCase() + renderStatus.slice(1).toLowerCase())
    : renderStatus;

  let meta = STATUS_STYLES[normalizedStatus as keyof typeof STATUS_STYLES] || STATUS_STYLES.Pending;

  // Override for occupation nodes that are 'Submitted'
  if (normalizedStatus === "Submitted" && phaseColor === "#8509faff") {
    meta = STATUS_STYLES["Occupation Submitted"];
  }
  const [imgError, setImgError] = useState(false);

  const isPending = renderStatus === 'Pending' && !isDisabled;
  const isRejected = renderStatus === 'Rejected' || renderStatus === 'Declined';
  const displayColor = (isDisabled || renderStatus === 'Upcoming') ? "#94A3B8" : ((isPending || isRejected) ? meta.color : (phaseColor || meta.color));
  const displayBg = (isDisabled || renderStatus === 'Upcoming') ? "#F8FAFC" : ((isPending || isRejected) ? meta.bg : `${phaseColor || meta.color}15`);
  const displayRing = (isDisabled || renderStatus === 'Upcoming') ? "#E2E8F0" : ((isPending || isRejected) ? meta.ringColor : (phaseColor || meta.ringColor));
  const displayIcon = (isDisabled || renderStatus === 'Upcoming') ? "" : meta.icon;
  const displayLabel = (isDisabled || renderStatus === 'Upcoming') ? "Waiting" : meta.label;

  return (
    <div
      style={{
        minWidth: 100, // Balanced width
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "0 2px", // Tightened horizontal padding
        position: "relative",
      }}
    >
      {/* Step Name Indication */}
      {appr.stepName && (
        <div style={{
          position: 'absolute',
          top: -20,
          whiteSpace: 'nowrap',
          fontSize: '9px',
          fontWeight: 800,
          color: phaseColor || '#6366F1',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          {appr.stepName}
        </div>
      )}

      <div
        style={{
          width: 46, // Restored to a better size
          height: 46,
          borderRadius: "50%",
          overflow: "hidden",
          border: `2px solid ${displayRing}`,
          background: "#fff",
          boxShadow: isCurrentPending && !isDisabled
            ? `0 0 0 2px ${displayBg}, 0 4px 12px -2px ${displayRing}40`
            : "0 2px 4px rgba(0,0,0,0.05)",
          animation: isCurrentPending && !isDisabled ? "pulse-ring 2s infinite" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          position: "relative",
          zIndex: 2,
        }}
      >
        {(() => {
          if (appr.isMilestone) {
            return (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: displayColor,
                color: '#fff',
                fontSize: 18,
                fontWeight: 900
              }}>
                ✦
              </div>
            );
          }
          const imgSource = appr.avatar || appr.image;
          if (imgSource && !imgError) {
            const isBase64 = !imgSource.startsWith("data:") && !imgSource.startsWith("http") && (imgSource.length > 100 || imgSource.startsWith("/9j/"));
            const formattedSrc = imgSource.startsWith("data:") || imgSource.startsWith("http")
              ? imgSource
              : (isBase64 ? `data:image/jpeg;base64,${imgSource}` : imgSource);
            return (
              <img
                src={formattedSrc}
                alt={appr.name}
                onError={() => setImgError(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: (renderStatus === 'Upcoming' || isDisabled) ? 'grayscale(100%) opacity(0.4)' : 'none',
                }}
              />
            );
          }
          return (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${displayBg} 0%, #ffffff 100%)`,
              color: displayColor,
              fontSize: 16,
              fontWeight: 700,
            }}>
              {appr.name?.charAt(0) || '?'}
            </div>
          );
        })()}

        {/* Status Mini Indicator */}
        {!isDisabled && displayIcon && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: displayColor,
            border: '2px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 8,
            fontWeight: 900,
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {displayIcon}
          </div>
        )}
      </div>

      {/* Name */}
      <div
        style={{
          marginTop: 6,
          fontSize: 11,
          fontWeight: 700,
          width: 110, // Increased width for better display
          lineHeight: '1.2',
          textAlign: 'center',
          color: (renderStatus === 'Upcoming' || isDisabled) ? '#94A3B8' : '#1E293B',
          wordWrap: 'break-word',
        }}
      >
        {appr.name}
      </div>

      {/* Status Label */}
      <span
        style={{
          marginTop: 2,
          fontSize: 8.5,
          padding: "1px 6px",
          borderRadius: 4,
          background: displayBg,
          color: displayColor,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
          opacity: (renderStatus === 'Upcoming' || isDisabled) ? 0.6 : 1,
          border: `1px solid ${displayColor}20`
        }}
      >
        {displayLabel}
      </span>
    </div>
  );
};

/* ------------------ MAIN ------------------ */
const ApproverTracker: React.FC<{ approvers?: Approver[]; showScrollbar?: boolean }> = ({
  approvers,
  showScrollbar = false,
}) => {
  const [open, setOpen] = useState(false);

  const listToFilter: Approver[] = approvers || [];

  const rawList: Approver[] = listToFilter.filter((appr: Approver) => {
    return !!appr && !!appr.name;
  });

  if (!rawList || rawList.length === 0) {
    return null;
  }

  // Identify the index of the LAST completed node to handle data inconsistencies
  let lastCompletedIndex = -1;
  rawList.forEach((appr, idx) => {
    const s = appr.status?.toUpperCase();
    if (s === "APPROVED" || s === "SUBMITTED" || s === "CONFIRMED") {
      lastCompletedIndex = idx;
    }
  });

  let foundActivePending = false;
  let hasBeenRejected = false;

  const processList = rawList.map((appr, idx) => {
    const statusUpper = appr.status?.toUpperCase();
    let renderStatus: any = appr.status;
    let isCurrentPending = false;

    let isDisabled = hasBeenRejected;
    if (statusUpper === "REJECTED" || statusUpper === "DECLINED") {
      hasBeenRejected = true;
    }

    // If this node is PENDING but a LATER node is already completed, 
    // it means this step is effectively completed in the workflow.
    if (idx < lastCompletedIndex && (statusUpper === "PENDING" || statusUpper === "AWAITING RESPONSE" || statusUpper === "AWAITING_RESPONSE" || statusUpper === "AWAITING")) {
      renderStatus = "Confirmed";
    }

    const currentStatusUpper = renderStatus?.toUpperCase();
    if (currentStatusUpper === "PENDING" || currentStatusUpper === "AWAITING RESPONSE" || currentStatusUpper === "AWAITING_RESPONSE" || currentStatusUpper === "AWAITING") {
      if (!foundActivePending && !isDisabled) {
        foundActivePending = true;
        isCurrentPending = true;
        renderStatus = "Pending";
      } else {
        renderStatus = "Upcoming";
      }
    }

    return { ...appr, renderStatus, isCurrentPending, isDisabled };
  });

  const renderTracker = () => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          gap: 0,
          overflowX: showScrollbar ? "scroll" : "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: showScrollbar ? "auto" : "none",
          padding: "20px 0 8px 0",
        }}
        className={showScrollbar ? "approval-history-scrollbar" : "no-scrollbar"}
        tabIndex={showScrollbar ? 0 : undefined}
        role={showScrollbar ? "region" : undefined}
        aria-label={showScrollbar ? "Approval history: scroll horizontally to view all stages" : undefined}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {processList.map((appr, idx) => {
            const isLast = idx === processList.length - 1;
            const currentStatusUpper = appr.renderStatus?.toUpperCase();
            const isCompleted = currentStatusUpper === "APPROVED" || currentStatusUpper === "SUBMITTED" || currentStatusUpper === "CONFIRMED";

            let foundWillingness = false;
            let isWillingnessStart = false;
            let foundOccupation = false;
            let isOccupationStart = false;
            let foundFinance = false;
            let isFinanceStart = false;

            for (let i = 0; i <= idx; i++) {
              if (processList[i].stepName === 'Willingness') {
                foundWillingness = true;
                if (i === idx) isWillingnessStart = true;
              }
              if (processList[i].stepName === 'Occupation') {
                foundOccupation = true;
                if (i === idx) isOccupationStart = true;
              }
              if (processList[i].stepName === 'Finance') {
                foundFinance = true;
                if (i === idx) isFinanceStart = true;
              }
            }

            // Look ahead to color the connector leading TO next phase
            let willBeOccupation = false;
            if (idx + 1 < processList.length && processList[idx + 1].stepName === 'Occupation') {
              willBeOccupation = true;
            }
            let willBeFinance = false;
            if (idx + 1 < processList.length && processList[idx + 1].stepName === 'Finance') {
              willBeFinance = true;
            }

            const phaseColor = foundFinance ? '#0d9488' : (foundOccupation ? '#a855f7' : (foundWillingness ? '#10b981' : '#4f46e5'));
            const connectorColor = (willBeFinance && (foundOccupation || foundWillingness)) ? '#0d9488' : ((willBeOccupation && foundWillingness) ? '#a855f7' : phaseColor);

            // Custom labels for phase start nodes
            const displayName = isFinanceStart
              ? (appr.name || "Finance Stage")
              : (isWillingnessStart
                ? (appr.status?.toUpperCase() === "CONFIRMED" || appr.status?.toUpperCase() === "APPROVED" || appr.status?.toUpperCase() === "ALLOTTED" || appr.status?.toUpperCase() === "ALLOTED"
                  ? `Willingness Confirmed`
                  : appr.status?.toUpperCase() === "REJECTED"
                    ? `Rejected by Approver`
                    : appr.status?.toUpperCase() === "DECLINED"
                      ? `Willingness Declined`
                      : `Willingness Consent`)
                : (isOccupationStart ? "Occupation Initiation" : appr.name));

            const displayStepBadge = isFinanceStart
              ? "FINANCE"
              : (isOccupationStart
                ? "OCCUPATION"
                : (isWillingnessStart
                  ? "WILLINGNESS"
                  : (idx === 0 ? (appr.stepName ? appr.stepName.toUpperCase() : "REGISTRATION") : undefined)));

            return (
              <div
                key={appr.id || appr.empId || idx}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexShrink: 0,
                }}
              >
                <div style={{ padding: '0 8px' }}>
                  <ApproverNode
                    appr={{ ...appr, name: displayName, stepName: displayStepBadge }}
                    renderStatus={appr.renderStatus}
                    isCurrentPending={appr.isCurrentPending}
                    isDisabled={appr.isDisabled}
                    phaseColor={phaseColor}
                  />
                </div>

                {!isLast && (
                  <div
                    style={{
                      width: 32, // Slightly wider connectors for better spacing
                      height: 1.5,
                      background: "#e2e8f0",
                      marginTop: 23,
                      position: "relative",
                      flexShrink: 0,
                      marginLeft: -8,
                      marginRight: -8,
                      zIndex: 1,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: (isCompleted && !appr.isDisabled) ? "100%" : "0%",
                        background: currentStatusUpper === 'REJECTED' ? STATUS_STYLES.Rejected.color : connectorColor,
                        transition: "width 0.8s ease-out",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="workflow-card"
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
          padding: "8px 16px",
          border: "1px solid #F1F5F9",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div>
            <h5 style={{
              margin: 0,
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#1E293B',
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <div style={{ width: 3, height: 14, background: '#6366F1', borderRadius: 1 }} />
              Events Status
            </h5>
          </div>
        </div>

        {renderTracker()}
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .approval-history-scrollbar::-webkit-scrollbar { height: 12px; }
        .approval-history-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 6px; }
        .approval-history-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; border: 2px solid #f1f5f9; }
        .approval-history-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
        .approval-history-scrollbar:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }
        @keyframes pulse-ring {
            0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
            70% { transform: scale(1.02); box-shadow: 0 0 0 12px rgba(99, 102, 241, 0); }
            100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
        }
        .workflow-card:hover {
            box-shadow: 0 30px 70px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.03);
            border-color: rgba(99, 102, 241, 0.2);
        }
      `}</style>
    </>
  );
};

export default ApproverTracker;
